import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import { Loading } from 'aha-components';
import { useTranslation } from 'react-i18next';
import axiosInstance from '@/apis/axiosInstance';
import {
  clearSignupReferralCodeInput,
  clearStoredReferralCode,
  getKeyFromUrlSearch,
  getSignupReferralCodeInput,
  getStoredReferralCode
} from '@/biz/tool';

type ReferralCodeGateProps = {
  children: React.ReactNode;
};

type AttemptState = {
  requestKey: string;
  finished: boolean;
};

const REFERRAL_CODE_REPORT_TIMEOUT_MS = 10_000;

const getReferralCodeToSend = () => {
  const signupInput = getSignupReferralCodeInput();
  if (signupInput !== null) return signupInput.trim();
  return (
    getKeyFromUrlSearch('REFERRALCODE') ||
    getStoredReferralCode() ||
    ''
  ).trim();
};

const ReferralCodeGate: React.FC<ReferralCodeGateProps> = ({ children }) => {
  const { t } = useTranslation();
  const { isLoaded, isSignedIn, userId } = useAuth();
  const location = useLocation();
  const requestInFlightRef = useRef(false);
  const attemptedRequestKeyRef = useRef<string | null>(null);
  const [attemptState, setAttemptState] = useState<AttemptState | null>(null);

  const referralCode = getReferralCodeToSend();
  const requestKey = userId && referralCode ? `${userId}:${referralCode}` : '';
  const isOnboardingPath =
    location.pathname === '/onboarding' ||
    location.pathname === '/onboarding/connect-socials';

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !requestKey || !referralCode) return;
    if (requestInFlightRef.current) return;
    if (attemptedRequestKeyRef.current === requestKey) return;

    requestInFlightRef.current = true;
    attemptedRequestKeyRef.current = requestKey;
    setAttemptState({ requestKey, finished: false });

    void axiosInstance
      .post(
        '/user/referralCode',
        { referralCode },
        { timeout: REFERRAL_CODE_REPORT_TIMEOUT_MS }
      )
      .then(({ data }) => {
        if (data?.code === 0) {
          clearSignupReferralCodeInput();
          clearStoredReferralCode();
          return;
        }
        console.error('setReferralCode error: ', data?.message || data);
      })
      .catch((error) => {
        // 保留待上报邀请码，下次重新加载应用时再尝试。
        console.error('setReferralCode error: ', error);
      })
      .finally(() => {
        requestInFlightRef.current = false;
        setAttemptState({ requestKey, finished: true });
      });
  }, [isLoaded, isSignedIn, referralCode, requestKey]);

  const isCurrentAttemptFinished =
    attemptState?.requestKey === requestKey && attemptState.finished;
  const shouldWaitBeforeOnboarding =
    isLoaded &&
    isSignedIn &&
    isOnboardingPath &&
    Boolean(requestKey) &&
    !isCurrentAttemptFinished;

  if (shouldWaitBeforeOnboarding) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center">
        <Loading width={50} height={50} color="#000" />
        <div className="mt-2 text-base text-gray-500">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ReferralCodeGate;
